import { compactCurrencyFormatter } from "../../../utils/formatter/formatter";

import {
  aggregateCoverage,
  aggregateGiro,
  aggregateSellThrough,
  buildCoverage,
  buildStockHealth,
  comparableCapitalChange,
  enrichCoverageBase,
  enrichTurnoverBase
} from "./business_rules/inventory/inventory";

import { aggregateMargin } from "./business_rules/profit/profit";
import { aggregateAvgPrice, aggregateMarkdown, aggregateMarkup } from "./business_rules/pricing/pricing";
import { groupBy, percentChange, sum } from "./business_rules/shared";

const PERCENT_IN_POINTS = false;

export const ALL_KEY = "all";
export const DEFAULT_PERIOD = 9;

export const PERIOD_OPTIONS = [
  { key: 1, label: "Mês atual" },
  { key: 3, label: "3 meses" },
  { key: 6, label: "6 meses" },  
  { key: 9, label: "9 meses" },
  { key: 12, label: "12 meses" },
];

const PERIOD_CONFIG = {
  1: { kpiMonths: 1, chartMonths: 1 },
  3: { kpiMonths: 3, chartMonths: 3 },
  6: { kpiMonths: 6, chartMonths: 6 },  
  9: { kpiMonths: 9, chartMonths: 9 },
  12: { kpiMonths: 12, chartMonths: 12 },
};

const MONTH_LABELS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const CLUSTER_KEYS = ["A", "B", "C", "D", "Z"];
const RANKED_CLUSTER_KEYS = ["A", "B", "C", "D"];
const DEFAULT_CLUSTER_THRESHOLDS = {
  A: { min: 0, max: 0.5 },
  B: { min: 0.5, max: 0.7 },
  C: { min: 0.7, max: 0.9 },
  D: { min: 0.9, max: 1 },
  Z: { min: 0, max: 0 },
};

const COMPLETE_MONTH_MIN_SHARE = 0.5;
//const COVERAGE_EXAMPLE = "Exemplo: o produto A tem R$ 9 mil em estoque e cobertura de 49 dias; o B tem R$ 3 mil e 34 dias. Somamos dias × estoque de cada um (49 × 9 mil + 34 × 3 mil = 543 mil) e dividimos pelo estoque total (12 mil): 543 ÷ 12 = 45 dias de cobertura. Considera só o mês atual, independentemente do período filtrado.";
const UNCLASSIFIED = "Sem classificação";
const ITEMS_UNIT = "items";

export const chartDivisor = (unit) => (unit === ITEMS_UNIT ? 1_000 : 1_000_000);

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const optionalNum = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toFraction = (value) => (PERCENT_IN_POINTS ? num(value) / 100 : num(value));
const toPoints = (value) => (PERCENT_IN_POINTS ? num(value) : num(value) * 100);
const optionalFraction = (value) => (value === null ? null : toFraction(value));
const optionalPoints = (value) => (value === null ? null : toPoints(value));

const monthIndex = (year, month) => num(year) * 12 + (num(month) - 1);

const extractProductHierarchies = (data) =>
  [...new Map(extractRows(data).map((row) => [String(row?.productId), row?.productHierarchy ?? {}])).values()];

const extractRows = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.formattedAnalytics)) return data.formattedAnalytics;
  if (data && typeof data === "object") return Object.values(data).find(Array.isArray) ?? [];
  return [];
};

const extractClusterThresholds = (data) => {
  const source = extractRows(data)[0]?.clusterPerformance?.clusterPerformance;

  return Object.fromEntries(CLUSTER_KEYS.map((key) => {
    const fallback = DEFAULT_CLUSTER_THRESHOLDS[key];
    const range = source?.[key];
    return [key, {
      min: optionalNum(range?.min) ?? fallback.min,
      max: optionalNum(range?.max) ?? fallback.max,
    }];
  }));
};

const normalizeRows = (data, unit) =>
  extractRows(data).map((row) => {
    const indicators = row?.profitIndicators ?? {};
    const coverage = row?.coverage ?? {};

    return {
      productId: String(row?.productId),
      idx: monthIndex(indicators.year, indicators.month),
      month: num(indicators.month),
      realized: num(indicators.realized),
      achieved: num(unit === ITEMS_UNIT ? indicators.soldUnits : indicators.realized),
      inventory: optionalNum(unit === ITEMS_UNIT ? indicators.inventoryUnits : indicators.inventoryCurrency),
      clientPrediction: optionalNum(indicators.clientPrediction),
      marginPercent: optionalNum(indicators.marginPercent),
      markdownPercent: optionalNum(indicators.markdownPercent),
      turnover: optionalNum(indicators.turnover),
      inventoryCurrency: num(indicators.inventoryCurrency),
      inventoryUnits: optionalNum(indicators.inventoryUnits),
      soldUnits: num(indicators.soldUnits),
      avgPrice: num(indicators.avgPrice),
      markup: optionalNum(indicators.markup),
      sellThrough: optionalNum(indicators.sellThrough),
      coverageDays: optionalNum(indicators.coverageDays),
      updatedAt: indicators.updatedAt,
      ageRange: typeof coverage.range === "string" ? coverage.range.trim() : "",
      idealCoverageMin: num(coverage.idealCoverageMin),
      idealCoverageMax: num(coverage.idealCoverageMax),
    };
  });

const productPath = (product, levels) => {
  const path = [];
  for (const level of levels) {
    const value = product?.[level];
    if (value === undefined || value === null || value === "") break;
    path.push(String(value));
  }
  return path;
};

const isPrefix = (prefix, full) => prefix.length <= full.length && prefix.every((segment, i) => full[i] === segment);

const isProductSelected = (path, selectedPaths) => {
  if (!selectedPaths?.length) return true;

  let governing = null;
  selectedPaths.forEach((entry) => {
    if (isPrefix(entry.path, path) && (!governing || entry.path.length > governing.path.length)) governing = entry;
  });

  return governing ? !governing.excluded : false;
};

export const buildHierarchyOptions = (data, levels) => {
  const detailsByLevel = Object.fromEntries(levels.map((level) => [level, []]));
  const seen = new Set();

  extractProductHierarchies(data).forEach((product) => {
    const path = productPath(product, levels);

    path.forEach((name, index) => {
      const currentPath = path.slice(0, index + 1);
      const key = `${index}\u0000${currentPath.join("\u0000")}`;
      if (seen.has(key)) return;

      seen.add(key);
      detailsByLevel[levels[index]].push({ path: currentPath, name });
    });
  });

  levels.forEach((level) => detailsByLevel[level].sort((a, b) => a.name.localeCompare(b.name, "pt-BR")));

  return detailsByLevel;
};

export const buildGroupOptions = (data, levels) => {
  const topLevel = levels?.[0];
  const names = new Set();

  extractProductHierarchies(data).forEach((product) => {
    const value = topLevel ? product?.[topLevel] : null;
    if (value) names.add(String(value));
  });

  return [
    { key: ALL_KEY, label: "Todas" },
    ...[...names].sort((a, b) => a.localeCompare(b, "pt-BR")).map((name) => ({ key: name, label: name })),
  ];
};

const summarizeMeta = (rows) => {
  const withMeta = rows.filter((row) => row.clientPrediction !== null);
  if (!withMeta.length) return { meta: null, atingimento: null };

  const meta = sum(withMeta, (row) => row.clientPrediction);

  return {
    meta,
    atingimento: meta ? sum(rows, (row) => row.achieved) / meta : null,
  };
};

const summarize = (rows, currentIdx) => {
  const latestIdx = rows.reduce((max, row) => Math.max(max, row.idx), -Infinity);
  const venda = sum(rows, (row) => row.realized);
  const { meta, atingimento } = summarizeMeta(rows);
  const currentRows = rows.filter((row) => row.idx === (currentIdx ?? latestIdx));
  const giroAtual = currentRows.length ? aggregateGiro(currentRows) : null;

  return {
    count: rows.length,
    venda,
    meta,
    metaPercent: atingimento,
    capital: sum(rows.filter((row) => row.idx === latestIdx), (row) => row.inventoryCurrency),
    vendaPecas: sum(rows, (row) => row.soldUnits),
    precoMedio: aggregateAvgPrice(rows),
    margem: aggregateMargin(rows, optionalFraction),
    markup: aggregateMarkup(rows, optionalFraction),
    giro: aggregateGiro(rows),
    giroAtual,
    sellThrough: aggregateSellThrough(rows, optionalFraction),
    cobertura: aggregateCoverage(currentRows),
    remarcacao: aggregateMarkdown(rows, optionalFraction),
  };
};

const hasDetailData = (row) => row.marginPercent !== null || row.turnover !== null || row.inventoryUnits !== null;

const findCompleteMonths = (rows) => new Set(
  [...groupBy(rows, (row) => row.idx).entries()]
    .filter(([, monthRows]) => monthRows.filter(hasDetailData).length / monthRows.length >= COMPLETE_MONTH_MIN_SHARE)
    .map(([idx]) => idx)
);

const buildKpis = (current, previous, { detailComparable, capitalChange }) => {
  const hasPrevious = previous.count > 0;
  const canCompareDetail = hasPrevious && detailComparable;

  return [
    {
      label: "Venda líquida", icon: "layers", value: compactCurrencyFormatter(current.venda),
      delta: hasPrevious ? percentChange(current.venda, previous.venda) : null, deltaUnit: "%",
    },
    {
      label: "Margem", icon: "percent", value: current.margem === null ? "—" : `${(current.margem * 100).toFixed(1)}%`,
      delta: canCompareDetail && current.margem !== null && previous.margem !== null ? (current.margem - previous.margem) * 100 : null,
      deltaUnit: " p.p.",
    },
    {
      label: "Giro", icon: "refresh-cw", value: current.giro === null ? "—" : current.giro.toFixed(1),
      delta: canCompareDetail && current.giro !== null && previous.giro !== null ? current.giro - previous.giro : null, deltaUnit: "x",
    },
    {
      label: "Capital em estoque", icon: "credit-card", value: compactCurrencyFormatter(current.capital),
      delta: canCompareDetail ? capitalChange : null, deltaUnit: "%", inverse: true,
    },
  ];
};

const buildChartData = (rows, latestIdx, months, divisor) => {
  const data = [];

  for (let idx = latestIdx - months + 1; idx <= latestIdx; idx += 1) {
    const monthRows = rows.filter((row) => row.idx === idx);

    const { meta, atingimento } = summarizeMeta(monthRows);

    data.push({
      month: MONTH_LABELS[((idx % 12) + 12) % 12],
      realizado: sum(monthRows, (row) => row.achieved) / divisor,
      meta: meta === null ? null : meta / divisor,
      atingimento,
      margemPct: aggregateMargin(monthRows, optionalPoints),
      remarcacaoPct: aggregateMarkdown(monthRows, optionalPoints),
    });
  }

  return data;
};

const buildMetaSummary = (rows, latestIdx, months) => {
  const chartRows = rows.filter((row) => row.idx > latestIdx - months && row.idx <= latestIdx);
  return { realizado: sum(chartRows, (row) => row.achieved), ...summarizeMeta(chartRows) };
};

const toCategoryRow = (id, name, currentRows, previousRows, totalVenda, currentIdx) => {
  const current = summarize(currentRows, currentIdx);
  const previous = summarize(previousRows);

  return {
    id,
    name,
    venda: current.venda,
    share: totalVenda ? current.venda / totalVenda : 0,
    lastYear: previous.venda ? current.venda / previous.venda - 1 : null,
    vendaPecas: current.vendaPecas,
    precoMedio: current.precoMedio,
    margem: current.margem,
    markup: current.markup,
    giro: current.giro,
    sellThrough: current.sellThrough,
    capitalEmEstoque: current.capital,
    cobertura: current.cobertura,
    remarcacao: current.remarcacao,
    metaPercent: current.metaPercent,
  };
};


const buildCategories = ({ windowRows, previousRows, currentIdx, levels, productsById }) => {
  const [topLevel, secondLevel] = levels;
  const levelName = (row, level) => (level ? productsById.get(row.productId)?.[level] : null) || UNCLASSIFIED;

  const totalVenda = sum(windowRows, (row) => row.realized);
  const previousByCategory = groupBy(previousRows, (row) => levelName(row, topLevel));

  const categories = [...groupBy(windowRows, (row) => levelName(row, topLevel)).entries()].map(([name, rows]) => {
    const previous = previousByCategory.get(name) ?? [];
    const previousBySub = groupBy(previous, (row) => levelName(row, secondLevel));

    const subgroups = [...groupBy(rows, (row) => levelName(row, secondLevel)).entries()]
      .map(([subName, subRows]) => toCategoryRow(`${name}-${subName}`, subName, subRows, previousBySub.get(subName) ?? [], totalVenda, currentIdx))
      .sort((a, b) => b.venda - a.venda);

    return { ...toCategoryRow(name, name, rows, previous, totalVenda, currentIdx), subgroups };
  });

  return categories.sort((a, b) => b.venda - a.venda);
};

const clusterForCumulativeShare = (cumulativeFraction, thresholds) =>
  RANKED_CLUSTER_KEYS.find((key) => cumulativeFraction <= thresholds[key].max)
  ?? RANKED_CLUSTER_KEYS[RANKED_CLUSTER_KEYS.length - 1];

const buildClusters = (windowRows, thresholds) => {
  const latestByProduct = new Map();
  windowRows.forEach((row) => {
    const current = latestByProduct.get(row.productId);
    if (!current || row.idx > current.idx) latestByProduct.set(row.productId, row);
  });

  const salesByProduct = new Map();
  windowRows.forEach((row) => salesByProduct.set(row.productId, (salesByProduct.get(row.productId) ?? 0) + row.realized));

  const products = [...latestByProduct.values()].map((row) => ({
    productId: row.productId,
    venda: salesByProduct.get(row.productId) ?? 0,
    estoque: row.inventoryCurrency,
  }));

  const semVendaComEstoque = products.filter((product) => product.venda <= 0 && product.estoque > 0);
  const totalVendaRanking = sum(products, (product) => (product.venda > 0 ? product.venda : 0));

  let acumulado = 0;
  const ranked = products
    .filter((product) => product.venda > 0)
    .sort((a, b) => b.venda - a.venda)
    .map((product) => {
      acumulado += product.venda;
      const cumulativeFraction = totalVendaRanking ? acumulado / totalVendaRanking : 0;
      return { ...product, cluster: clusterForCumulativeShare(cumulativeFraction, thresholds) };
    });

  const items = [...ranked, ...semVendaComEstoque.map((product) => ({ ...product, cluster: "Z" }))];

  const totalVenda = sum(items, (item) => item.venda);
  const totalEstoque = sum(items, (item) => item.estoque);

  const clusters = CLUSTER_KEYS.map((key) => {
    const members = items.filter((item) => item.cluster === key);
    return {
      key,
      percentVenda: totalVenda ? sum(members, (item) => item.venda) / totalVenda : 0,
      percentEstoque: totalEstoque ? sum(members, (item) => item.estoque) / totalEstoque : 0,
      qtdItens: members.length,
      percentItens: items.length ? members.length / items.length : 0,
    };
  }).filter((cluster) => cluster.qtdItens > 0);

  return {
    clusters,
    total: {
      percentVenda: sum(clusters, (cluster) => cluster.percentVenda),
      percentEstoque: sum(clusters, (cluster) => cluster.percentEstoque),
      qtdItens: sum(clusters, (cluster) => cluster.qtdItens),
      percentItens: sum(clusters, (cluster) => cluster.percentItens),
    },
  };
};

const formatUpdatedAt = (rows) => {
  const latest = rows.reduce((max, row) => {
    const time = row.updatedAt ? new Date(row.updatedAt).getTime() : NaN;
    return Number.isFinite(time) && time > max ? time : max;
  }, -Infinity);

  if (!Number.isFinite(latest)) return "—";

  const date = new Date(latest);
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}`;
};

export const buildProductAnalytics = ({ data, unit, levels, period, group, hierarchySelection }) => {
  const rows = enrichCoverageBase(enrichTurnoverBase(normalizeRows(data, unit)));
  if (!rows.length) return { hasData: false };

  const productsById = new Map(extractRows(data).map((row) => [String(row?.productId), row?.productHierarchy ?? {}]));
  const selectionCache = new Map();

  const isIncluded = (row) => {
    if (!selectionCache.has(row.productId)) {
      const product = productsById.get(row.productId);
      const matchesGroup = group === ALL_KEY || (levels[0] && String(product?.[levels[0]]) === group);
      selectionCache.set(row.productId, matchesGroup && isProductSelected(productPath(product, levels), hierarchySelection));
    }
    return selectionCache.get(row.productId);
  };

  const filtered = rows.filter(isIncluded);
  const latestIdx = rows.reduce((max, row) => Math.max(max, row.idx), -Infinity);
  const config = PERIOD_CONFIG[period] || PERIOD_CONFIG[DEFAULT_PERIOD];

  const inWindow = (row, endIdx) => row.idx > endIdx - config.kpiMonths && row.idx <= endIdx;
  const windowRows = filtered.filter((row) => inWindow(row, latestIdx));
  const previousRows = filtered.filter((row) => inWindow(row, latestIdx - 12));

  const current = summarize(windowRows, latestIdx);
  const previous = summarize(previousRows);

  const completeMonths = findCompleteMonths(rows);
  const detailComparable = Array.from({ length: config.kpiMonths }, (_, i) => latestIdx - 12 - i)
    .every((idx) => completeMonths.has(idx));
  const capitalChange = comparableCapitalChange(filtered, latestIdx, latestIdx - 12);
  const category = buildCategories({ windowRows, previousRows, currentIdx: latestIdx, levels, productsById });
  const { clusters, total: clusterTotal } = buildClusters(windowRows, extractClusterThresholds(data));

  return {
    hasData: true,
    generatedAt: formatUpdatedAt(rows),
    kpis: buildKpis(current, previous, { detailComparable, capitalChange }),
    chartData: buildChartData(filtered, latestIdx, config.chartMonths, chartDivisor(unit)),
    metaSummary: buildMetaSummary(filtered, latestIdx, config.chartMonths),
    categories: category,
    categoryTotal: group === ALL_KEY ? { ...toCategoryRow("total", "Total", windowRows, previousRows, current.venda, latestIdx), share: 1 } : null,
    defaultExpandedId: group === ALL_KEY ? null : group,
    clusters,
    clusterTotal,
    stockHealth: buildStockHealth(windowRows, latestIdx),
    coverage: buildCoverage({
      windowRows,
      current,
      currentIdx: latestIdx,
      categoryName: group === ALL_KEY ? "Todas" : group,
      resolveProduct: (row) => {
        const product = productsById.get(row.productId);
        const path = productPath(product, levels);

        return {
          name: path[path.length - 1] ?? `Produto ${row.productId}`,
          categoria: (levels[0] && product?.[levels[0]]) || UNCLASSIFIED,
        };
      },
    }),
  };
};